import type { CollectionDocument } from "./document";

/**
 * Undo/redo for the editing session.
 *
 * The document is one JSON object, so history is a stack of snapshots rather than
 * a log of operations — nothing to invert, nothing to replay, and "undo" restores
 * a rule *including its position* because position is part of the snapshot.
 *
 * In page memory only: the server keeps revisions, and a second versioning system
 * next to them would be a second thing to reconcile.
 */
export interface History {
  readonly past: readonly CollectionDocument[];
  readonly future: readonly CollectionDocument[];
  /** When the last entry was pushed, for coalescing keystrokes. */
  readonly lastPushAt: number;
}

export const emptyHistory: History = { past: [], future: [], lastPushAt: 0 };

/** Deep enough for a session; snapshots are small but not free. */
export const maxHistoryDepth = 60;

/** Keystrokes inside one field collapse into a single undo step. */
export const coalesceWindowMs = 600;

export function pushHistory(
  history: History,
  previous: CollectionDocument,
  options: { readonly coalesce: boolean; readonly now?: number } = { coalesce: false },
): History {
  const now = options.now ?? Date.now();
  // A structural edit always gets its own step; typing collapses while it is
  // still the same burst.
  if (options.coalesce && history.past.length > 0 && now - history.lastPushAt < coalesceWindowMs) {
    return { ...history, future: [], lastPushAt: now };
  }
  return {
    past: [...history.past, previous].slice(-maxHistoryDepth),
    future: [],
    lastPushAt: now,
  };
}

export function canUndo(history: History): boolean {
  return history.past.length > 0;
}

export function canRedo(history: History): boolean {
  return history.future.length > 0;
}

export function undo(
  history: History,
  current: CollectionDocument,
): { readonly history: History; readonly document: CollectionDocument } | null {
  const previous = history.past[history.past.length - 1];
  if (!previous) return null;
  return {
    history: {
      past: history.past.slice(0, -1),
      future: [current, ...history.future].slice(0, maxHistoryDepth),
      lastPushAt: 0,
    },
    document: previous,
  };
}

export function redo(
  history: History,
  current: CollectionDocument,
): { readonly history: History; readonly document: CollectionDocument } | null {
  const next = history.future[0];
  if (!next) return null;
  return {
    history: {
      past: [...history.past, current].slice(-maxHistoryDepth),
      future: history.future.slice(1),
      lastPushAt: 0,
    },
    document: next,
  };
}

/**
 * Ctrl/Cmd+Z, and Ctrl/Cmd+Shift+Z or Ctrl+Y for redo.
 *
 * Takes the four fields it reads rather than a `KeyboardEvent`, so the mapping is
 * testable without a DOM — a real event satisfies the shape.
 */
export function historyShortcut(event: {
  readonly key: string;
  readonly metaKey: boolean;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
}): "undo" | "redo" | null {
  const modifier = event.metaKey || event.ctrlKey;
  if (!modifier) return null;
  const key = event.key.toLowerCase();
  if (key === "z") return event.shiftKey ? "redo" : "undo";
  if (key === "y") return "redo";
  return null;
}
